/**
 * `<DeleteTeamDialog/>` — owner-initiated hard delete of a whole team.
 *
 * Calls `DELETE /api/teams/{team_id}` (issue #1275 — owner-only on the
 * server; the whole team cascades: cycles, rosters, submissions,
 * requests, settings. Member accounts are kept). Because this removes
 * a whole workspace for every member, the guard is stronger than the
 * account-delete dialog's literal "delete": the user must type the
 * exact team name before the destructive button enables.
 *
 * On success the caller's `onDeleted` runs (HomePage refetches the
 * team + workspace lists there); the dialog closes itself.
 */
import { Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { ApiError, apiFetch } from "../api/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "./ui";

export function DeleteTeamDialog({
  team,
  open,
  onOpenChange,
  onDeleted,
}: {
  team: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the typed guard whenever the dialog opens (or retargets to a
  // different team) so a previous confirmation can't carry over.
  useEffect(() => {
    if (open) {
      setConfirm("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const nameMatches = team !== null && confirm.trim() === team.name;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!team || !nameMatches) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/teams/${team.id}`, { method: "DELETE" });
      onOpenChange(false);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-danger">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete {team?.name ?? "this team"}
          </DialogTitle>
          <DialogDescription>
            This permanently deletes the team for every member — its rosters, submissions, leave and
            call requests, and settings. Member accounts are kept. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm-delete-team">
              Type <span className="font-mono text-danger">{team?.name}</span> to confirm
            </Label>
            <Input
              id="confirm-delete-team"
              autoFocus
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={busy || !nameMatches}>
              {busy ? "Deleting…" : "Delete team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
